import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { FaUsers, FaEye, FaPlus, FaDownload } from "react-icons/fa";
import styles from "../academicConfiguration/Academiclist.module.css";
import { CustomButton, CustomDropdown } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchClassOptions,
  fetchStaffClassAndSubjects,
  fetchSectionOptions,
  clearSectionOptions,
  GENDER_OPTIONS,
} from "../../../../redux/Masterdata";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const StudentList = () => {
  const { translations } = useSelector((state) => state.language);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const master_id = clientData?.master_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const roleId = Number(user?.role?.id);

  const {
    classOptions: reduxClassOptions,
    staffClassAndSubjectOptions,
    sectionOptions: reduxSectionOptions,
  } = useSelector((state) => state.masterData);

  const classOptions = useMemo(() => {
    // Staff data has one row per section/subject – keep a single entry per class
    const uniqueStaffClasses = Array.from(
      new Map(
        (staffClassAndSubjectOptions || []).map((item) => [
          `${item.class_id}-${item.class_name}`,
          item,
        ]),
      ).values(),
    );
    const sourceData =
      roleId === 3 ? uniqueStaffClasses : reduxClassOptions;

    return [
      {
        value: "",
        label: translations.labels?.selectClass || "Select Class",
      },
      ...sourceData.map((item) => ({
        value: item.class_id ?? item.value,
        label: item.class_name ?? item.label,
        classId: item.class_id ?? item.value,
        className: item.class_name ?? item.label,
        sectionId: item.section_id || "",
        subjectId: item.subject_id || "",
        subjectName: item.subject_name || "",
      })),
    ];
  }, [reduxClassOptions, staffClassAndSubjectOptions, roleId, translations]);

  const sectionOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectSection || "Select Section",
      },
      ...reduxSectionOptions,
    ],
    [reduxSectionOptions, translations],
  );

  const genderOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectGender || "Select Gender",
      },
      ...GENDER_OPTIONS.filter((opt) => opt.value !== ""),
    ],
    [translations],
  );

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedGender, setSelectedGender] = useState("");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  // Add ref for column filters (same as StaffList)
  const columnFiltersRef = useRef([]);

  useEffect(() => {
    columnFiltersRef.current = columnFilters;
  }, [columnFilters]);
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  useEffect(() => {
    if (roleId !== 3) {
      dispatch(
        fetchClassOptions({
          id: parseInt(school_id),
          academic_year: academic_year,
        }),
      );
    } else {
      dispatch(
        fetchStaffClassAndSubjects({
          staff_id: parseInt(master_id),
          class_id: "",
          section_id: "",
          subject_id: "",
        }),
      );
    }
  }, [dispatch, school_id, academic_year, master_id, roleId]);

  const handleClassChange = useCallback(
    (value) => {
      const classId = typeof value === "object" ? value?.value : value;
      setSelectedClass(classId);
      setSelectedSection("");

      dispatch(clearSectionOptions());

      if (classId) {
        dispatch(
          fetchSectionOptions({
            id: [parseInt(classId)],
            school_id: parseInt(school_id),
            staff_id: parseInt(master_id),
            subject_staff: 1,
          }),
        );
      }

      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch, school_id, master_id],
  );

  const handleSectionChange = useCallback((value) => {
    const sectionId = typeof value === "object" ? value?.value : value;
    setSelectedSection(sectionId);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleGenderChange = useCallback((value) => {
    const genderId = typeof value === "object" ? value?.value : value;
    setSelectedGender(genderId);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      let response;
      // Get the name filter value from column filters
      const nameFilter =
        columnFiltersRef.current.find((f) => f.id === "name")?.value || "";

      if (roleId !== 3) {
        const payload = {
          school_id: parseInt(school_id),
          student_class: selectedClass || "",
          section: selectedSection || "",
          gender: selectedGender || "",
          search: nameFilter,
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          academic_year_id: academic_year_id,
        };

        response = await AxiosInstance.post("/school/studentsList", payload);
      } else {
        const payload = {
          staff_id: parseInt(master_id),
          class_id: selectedClass || "",
          section: selectedSection || "",
          gender: selectedGender || "",
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          search: nameFilter,
        };

        response = await AxiosInstance.post(
          "/school/staff/studentList",
          payload,
        );
      }

      if (response.data?.status) {
        const students = response.data.data || [];
        setData(students);
        setRowCount(response.data.pagination?.total || students.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    roleId,
    school_id,
    master_id,
    academic_year_id,
    selectedClass,
    selectedSection,
    selectedGender,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  // Latest fetchStudents for handlers held inside the memoized columns
  const fetchStudentsRef = useRef(fetchStudents);
  fetchStudentsRef.current = fetchStudents;

  useEffect(() => {
    if (school_id) {
      fetchStudents();
    }
  }, [fetchStudents, apiSearch, school_id]);

  // Watch for search input being cleared
  useEffect(() => {
    if (searchInput === "" && apiSearch) {
      setApiSearch("");
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [searchInput, apiSearch]);

  const handleFilterKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setPagination((prev) => {
        if (prev.pageIndex === 0) {
          fetchStudentsRef.current();
          return prev;
        }
        return { ...prev, pageIndex: 0 };
      });
    }
  }, []);
  // Add Enter key handler for column filters
  // useEffect(() => {
  //   const handleKeyDown = (e) => {
  //     if (e.key !== "Enter") return;

  //     const isFilterInput =
  //       e.target.tagName === "INPUT" &&
  //       (e.target.closest("thead") ||
  //         e.target.closest(".MuiTableHead-root") ||
  //         e.target.closest('[class*="MRT_TableHeadCell"]') ||
  //         e.target.closest('[class*="Header"]'));

  //     if (isFilterInput) {
  //       e.preventDefault();
  //       setPagination((prev) => {
  //         if (prev.pageIndex === 0) {
  //           fetchStudents();
  //           return prev;
  //         }
  //         return { ...prev, pageIndex: 0 };
  //       });
  //     }
  //   };

  //   document.addEventListener("keydown", handleKeyDown, true);
  //   return () => document.removeEventListener("keydown", handleKeyDown, true);
  // }, [fetchStudents]);

  const handleColumnFiltersChange = (updater) => {
    setColumnFilters((prev) => {
      const newFilters =
        typeof updater === "function" ? updater(prev) : updater;
      return newFilters;
    });
  };

  const handleView = useCallback(
    (student) => {
      navigate(`/user/studentCreate`, {
        state: { ID: student.stud_id, studentData: student, mode: "view" },
      });
    },
    [navigate],
  );

  const handleAdd = () => {
    navigate("/user/studentCreate");
  };

  // CSV Download helper
  const convertToCSV = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return "";

    const headers = [
      "Student ID",
      "Student Name",
      "Email Address",
      "Class",
      "Section",
      "Gender",
      "Blood Group",
      "Medical Condition",
    ];

    const rows = dataArray.map((student) => {
      const classOption = reduxClassOptions.find(
        (opt) => opt.value === student.class?.toString(),
      );
      const className = classOption?.label || student.class || "";
      const gender = student.gender
        ? student.gender.charAt(0).toUpperCase() + student.gender.slice(1)
        : "";

      return [
        student.student_id || "",
        student.name || "",
        student.email || "",
        className,
        student.student_section || "",
        gender,
        student.blood_group || "",
        student.medical_condition || "",
      ]
        .map((field) => {
          const stringField = String(field);
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          if (
            stringField.includes(",") ||
            stringField.includes('"') ||
            stringField.includes("\n")
          ) {
            return `"${stringField.replace(/"/g, '""')}"`;
          }
          return stringField;
        })
        .join(",");
    });

    return [headers.join(","), ...rows].join("\n");
  };

  const downloadCSV = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

 const handleDownload = async () => {
  try {
    setDownloadLoading(true);
    let response;

    if (roleId !== 3) {
      const payload = {
        school_id: parseInt(school_id),
        student_class: selectedClass || "",
        section: selectedSection || "",
        gender: selectedGender || "",
        search: apiSearch || "",
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        academic_year_id: academic_year_id,
      };

      response = await AxiosInstance.post("/school/studentsList", payload);
    } else {
      const payload = {
        staff_id: parseInt(master_id),
        class_id: selectedClass || "",
        section: selectedSection || "",
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      };

      response = await AxiosInstance.post(
        "/school/staff/studentList",
        payload,
      );
    }

    if (response.data?.status) {
      const students = response.data.data || [];

      if (students.length === 0) {
        setModalState({
          open: true,
          type: "success",
          message: response.data?.message || "No data available to download.",
          redirectPath: "",
        });
        return;
      }

      const csvContent = convertToCSV(students);
      downloadCSV(csvContent, "STUDENT_LIST.csv");
    } else {
      setModalState({
        open: true,
        type: "success",
        message: response.data?.message || "No data available to download.",
        redirectPath: "",
      });
    }
  } catch (error) {
    console.error("Error downloading students:", error);
    setModalState({
      open: true,
      type: "error",
      message: "Failed to download student list. Please try again.",
      redirectPath: "",
    });
  } finally {
    setDownloadLoading(false);
  }
};

  // const filteredData = useMemo(() => {
  //   if (columnFilters.length === 0) return data;

  //   return data.filter((row) => {
  //     return columnFilters.every((filter) => {
  //       const value = row[filter.id];
  //       if (value === null || value === undefined) return false;
  //       return String(value)
  //         .toLowerCase()
  //         .includes(String(filter.value).toLowerCase());
  //     });
  //   });
  // }, [data, columnFilters]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "student_id",
        header: translations.labels?.studentId || "Student ID",
        enableSorting: true,
        enableColumnFilter: false,
        size: 120,
        muiFilterTextFieldProps: {
          placeholder: translations.labels?.searchStudentId || "Search ID",
          onKeyDown: handleFilterKeyDown,
        },
      },
      {
        accessorKey: "name",
        header: translations.labels?.studentName || "Student Name",
        enableSorting: true,
        enableColumnFilter: true,
        size: 180,
        muiFilterTextFieldProps: {
          placeholder: translations.labels?.searchStudentName || "Search Name",
          onKeyDown: handleFilterKeyDown,
        },
      },
      {
        accessorKey: "email",
        header: translations.labels?.emailAddress || "Email Address",
        enableSorting: true,
        enableColumnFilter: false,
        size: 180,
        muiFilterTextFieldProps: {
          placeholder: translations.labels?.searchEmail || "Search Email",
          onKeyDown: handleFilterKeyDown,
        },
      },
      {
        accessorKey: "class",
        header: translations.labels?.class || "Class",
        enableSorting: true,
        enableColumnFilter: false,
        size: 80,
        Cell: ({ cell }) => {
          const classId = cell.getValue();
          const classOption = reduxClassOptions.find(
            (opt) => opt.value === classId?.toString(),
          );
          return classOption?.label || classId || "-";
        },
        muiFilterTextFieldProps: {
          placeholder: translations.labels?.searchClass || "Search Class",
          onKeyDown: handleFilterKeyDown,
        },
      },
      {
        accessorKey: "student_section",
        header: translations.labels?.section || "Section",
        enableSorting: true,
        enableColumnFilter: false,
        size: 80,
        muiFilterTextFieldProps: {
          placeholder: translations.labels?.searchSection || "Search Section",
          onKeyDown: handleFilterKeyDown,
        },
      },
      {
        accessorKey: "gender",
        header: translations.labels?.gender || "Gender",
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? value.charAt(0).toUpperCase() + value.slice(1) : "-";
        },
        muiFilterTextFieldProps: {
          placeholder: translations.labels?.searchGender || "Search Gender",
          onKeyDown: handleFilterKeyDown,
        },
      },
      {
        accessorKey: "blood_group",
        header: translations.labels?.bloodGroup || "Blood Group",
        enableSorting: true,
        enableColumnFilter: false,
        size: 120,
        muiFilterTextFieldProps: {
          placeholder:
            translations.labels?.searchBloodGroup || "Search Blood Group",
          onKeyDown: handleFilterKeyDown,
        },
      },
      {
        accessorKey: "medical_condition",
        header: translations.labels?.medicalCondition || "Medical Condition",
        enableSorting: true,
        enableColumnFilter: false,
        size: 150,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        id: "actions",
        header: translations.labels?.actions || "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={translations.labels?.viewStudent || "View Student"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [translations, reduxClassOptions, handleFilterKeyDown, handleView],
  );
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaUsers />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.studentList || "Student List"}
          </h1>
        </div>

        {/* Filter Dropdowns */}
        <div className={styles.filterContainer || "row mb-3"}>
          <div className="col-lg-3 col-md-4 col-sm-6 px-2 mb-2">
            <CustomDropdown
              label={translations.labels?.class || "Class"}
              name="filterClass"
              value={selectedClass}
              onChange={handleClassChange}
              options={classOptions}
              placeholder={translations.labels?.selectClass || "Select Class"}
            />
          </div>
          <div className="col-lg-3 col-md-4 col-sm-6 px-2 mb-2">
            <CustomDropdown
              label={translations.labels?.section || "Section"}
              name="filterSection"
              value={selectedSection}
              onChange={handleSectionChange}
              options={sectionOptions}
              placeholder={
                translations.labels?.selectSection || "Select Section"
              }
              disabled={!selectedClass}
            />
          </div>
          <div className="col-lg-3 col-md-4 col-sm-6 px-2 mb-2">
            <CustomDropdown
              label={translations.labels?.gender || "Gender"}
              name="filterGender"
              value={selectedGender}
              onChange={handleGenderChange}
              options={genderOptions}
              placeholder={translations.labels?.selectGender || "Select Gender"}
            />
          </div>
        </div>

        {/* Actions */}
        {roleId !== 3 && (
          <div className={styles.actions}>
            <CustomButton
              type="button"
              variant="contained"
              className={styles.addButton}
              fullWidth={false}
              onClick={handleAdd}
            >
              <FaPlus style={{ marginRight: "8px" }} />
              {translations.labels?.addButton || "Add"}
            </CustomButton>
            <CustomButton
              type="button"
              variant="contained"
              className={styles.downloadButton}
              fullWidth={false}
              onClick={handleDownload}
              disabled={downloadLoading}
            >
              <FaDownload style={{ marginRight: "8px" }} />
              {downloadLoading
                ? "Downloading..."
                : translations.labels?.downloadButton || "Download"}
            </CustomButton>
          </div>
        )}

        {/* Table */}
        <CustomMRTTable
          columns={columns}
          data={data}
          loading={loading}
          pagination={pagination}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          placeholder={translations.labels.searchStudentName}
          onSearchEnter={(value) => {
            setApiSearch(value);
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
          onSearchClear={() => {
            setApiSearch("");
            setSearchInput("");
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          enableColumnFilters={true}
          noRecordsMessage={
            translations.labels?.noRecordsToDisplay || "No records to display"
          }
          enableSearch={false}
          // enableFiltering={false}
          manualFiltering={true} // ✅ ADD THIS LINE
        />
      </div>
    </>
  );
};

export default StudentList;
